/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  attach,
  createEffect,
  createEvent,
  createStore,
  sample,
  scopeBind,
  Store,
  type EventCallable,
  type Unit,
} from 'effector'
import { not } from 'patronum'

export function setIntervalTick<T>({
  init,
  $intervalMs,
  tick = createEvent<number>(), // tick event, ticks on every $catchupUpdateInterval
}: {
  init: Unit<T>
  $intervalMs: Store<number>
  tick?: EventCallable<number>
}) {
  const teardown = createEvent()

  // flag, which indicates that interval is ticking now
  const $ticking = createStore(false)

  // timeout id, which is used to clear timeout on teardown
  const $timeout = createStore<ReturnType<typeof setTimeout> | null>(null)

  // tick effect, which returns current timestamp, to pass it to `tick` event
  const tickFx = createEffect(() => Date.now())

  // wind up effect, which waits until next minute and then triggers tick effect
  const windFx = createEffect(
    ({
      now = Date.now(),
      intervalMs,
    }: {
      now?: number
      intervalMs: number
    }) => {
      const untilNextTick = intervalMs - (now % intervalMs)
      const bindedTickFx = scopeBind(tickFx, { safe: true })
      return setTimeout(bindedTickFx, untilNextTick)
    }
  )

  // unwind effect, which clears timeout on teardown
  const unwindFx = attach({
    source: $timeout,
    effect(timeout) {
      if (timeout != null) clearTimeout(timeout)
    },
  })

  // on `init` event, emit initial tick (which will then trigger wind up effect)
  sample({
    clock: init,
    filter: not($ticking),
    fn: () => true,
    target: [$ticking, tickFx],
  })

  // on `teardown` event, unwind clock
  sample({
    clock: teardown,
    fn: () => false,
    target: [$ticking, unwindFx],
  })

  // save timeout id
  sample({
    clock: windFx.doneData,
    target: $timeout,
  })

  // on tick effect done, emit tick event
  sample({
    clock: tickFx.doneData,
    target: tick,
  })

  // on tick event, wind up clock again
  sample({
    clock: tick,
    source: $intervalMs,
    filter: $ticking,
    fn: (intervalMs) => {
      return { intervalMs }
    },
    target: windFx,
  })

  return { tick, teardown }
}
